import { useCallback } from 'react';

import { setMustRefreshAuthentication } from '@redux/authentication';
import { resetRoom, setBamRoomId, setRoomUuid } from '@redux/room';
import { roomIdSelector } from '@redux/room/selectors';
import { shouldSkipStaffPasswordSelector } from '@redux/session/selectors';
import { setUserUuid, setUserType, resetUser } from '@redux/user';
import { captureException } from '@sentry/browser';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { PATHS } from 'constants/pathsB2b';
import { sendMultiScreenEventToTv } from 'services/helpers/multiScreen';
import { connectB2bRoom, disconnectB2bRoom, getUser } from 'services/networking/b2bRoom';
import { getBamRoomId } from 'services/networking/booking';
import { PlayerEvent } from 'shared-types/player-event';
import { B2bUserType } from 'shared-types/user';

import { useClearSession } from './useClearSession';

interface B2bAuthEventListeners {
  handleSignIn: (roomUuid: string | undefined) => void;
  handleSignOut: (roomUuid: string | null) => void;
}

// TODO Voir si fin de session ou déconnexion de l'user
export const useB2bAuthEventListeners = (): B2bAuthEventListeners => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const clearSession = useClearSession();

  const shouldSkipStaffPassword = useSelector(shouldSkipStaffPasswordSelector);
  const roomId = useSelector(roomIdSelector);

  const handleSignIn = useCallback(
    async (roomUuid: string | undefined) => {
      try {
        if (roomUuid) {
          await connectB2bRoom(roomUuid);
          dispatch(setRoomUuid(roomUuid));
          const user = await getUser(roomUuid);
          const userId = user?.id;
          const userType = user?.b2bUserType;

          dispatch(setUserUuid(userId));
          dispatch(setUserType(userType));

          if (userType === B2bUserType.BAM && roomId) {
            const { id } = await getBamRoomId(roomId);
            dispatch(setBamRoomId(id));
          }
          sendMultiScreenEventToTv(PlayerEvent.SYNCHRONIZE_USER_ID_ACCROSS_SCREEN, userId);
        }
      } catch (error) {
        captureException(error);
      }
    },
    [dispatch, roomId],
  );

  const handleSignOut = useCallback(
    async (roomUuid: string | null) => {
      try {
        roomUuid && (await disconnectB2bRoom(roomUuid));
        dispatch(resetRoom());
        dispatch(resetUser());
        dispatch(setMustRefreshAuthentication(false));
        clearSession();
        navigate(shouldSkipStaffPassword ? PATHS.SESSION : PATHS.STAFF);
      } catch (error) {
        captureException(error);
      }
    },
    [clearSession, dispatch, navigate, shouldSkipStaffPassword],
  );

  return { handleSignIn, handleSignOut };
};
